import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import InputText from 'components/forms/InputText';
import { useBreakpoint } from 'components/BreakpointProvider';
import SubmitButton from 'components/forms/SubmitButton';
import { mediaMSmMax } from 'helpers/breakpoints';
import ConfirmationIcon from 'assets/scss/fortawesome/icons/svg/confirmation.svg';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import { useInviteCaptainMutation } from 'components/pdp/v3/hooks';
import { useAssignCaptainMutation } from 'components/booking/mutationHooks';
import { useNearCaptainsForBookingQuery } from 'components/booking/queryHooks';
import { useCloseModal } from 'components/remodal/v2/hooks';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import Button from 'components/ui/Button/Button';
import CaptainCard from './CaptainCard';

const AssignCaptainModal = ({
  bookingId,
  boatId,
  onSuccess,
}: {
  bookingId: string;
  boatId?: string;
  onSuccess?: () => void;
}) => {
  const closeModal = useCloseModal();

  const [selectedCaptain, setSelectedCaptain] = useState<string | null>(null);

  const { mutate: assignCaptainMutate, isLoading: isAssigningCaptain } = useAssignCaptainMutation({
    onSuccess() {
      onSuccess?.();
    },
    onSettled() {
      closeModal();
    },
  });

  const { nearCaptains, isLoading } = useNearCaptainsForBookingQuery(bookingId);

  const submit = async () => {
    assignCaptainMutate({ bookingId, body: { captain_id: selectedCaptain } });
  };

  if (isLoading)
    return (
      <div
        style={{
          left: '50%',
          marginLeft: '-45px',
          top: '50%',
          position: 'absolute',
        }}
      >
        <LoadingSpinner color="#72D4BA" />
      </div>
    );

  return (
    <Wrapper>
      <Heading>Change captain</Heading>
      <Container>
        {nearCaptains.map((captain) => (
          <CardWrapper key={captain.public_id}>
            <CaptainCard selectedCaptain={selectedCaptain} setSelectedCaptain={setSelectedCaptain} captain={captain} />
          </CardWrapper>
        ))}
      </Container>
      <ButtonWrapper>
        <Button
          disabled={!selectedCaptain}
          loading={isAssigningCaptain}
          onClick={submit}
          outline
          text="Confirm to assign captain"
        />
      </ButtonWrapper>

      {boatId && <InviteCaptainForm boatId={boatId} />}
    </Wrapper>
  );
};

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  padding-top: 25px;
  justify-content: center;

  button span {
    min-width: 260px;
    padding: 0 20px;
  }

  button:disabled span {
    color: white !important;
  }
`;

const Wrapper = styled.div``;

const Heading = styled.h1`
  font-weight: 700;
  font-size: 25px;
  margin-bottom: 25px;
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 28px;

  ${mediaMSmMax} {
    gap: 16px;
  }
`;

const CardWrapper = styled.div`
  background: #f9fafe;
  display: flex;
  justify-content: center;
  padding: 25px 15px;
  width: calc(calc(100% - 56px) / 3);
  text-align: center;

  ${mediaMSmMax} {
    width: calc(calc(100% - 16px) / 2);
  }
`;

const InviteCaptainForm = ({ boatId }) => {
  const methods = useFormWithErrorTracking({
    mode: 'onSubmit',
  });
  const { handleSubmit, errors } = methods;
  const [captainInvited, setCaptainInvited] = useState(false);

  const inviteCaptainMutation = useInviteCaptainMutation();

  const processFormData = async (formValues) => {
    await inviteCaptainMutation.mutateAsync({
      boatId,
      body: formValues,
    });

    setCaptainInvited(true);
    setTimeout(() => {
      setCaptainInvited(false);
    }, 2000);
  };

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  if (captainInvited) {
    return (
      <InvitedWrapper>
        <ConfirmationIconStyled />
        <InvitedInfo>Your invitation has been sent</InvitedInfo>
      </InvitedWrapper>
    );
  }

  return (
    <InviteWrapper>
      <InviteHeading>Invite a Captain</InviteHeading>
      <FormProvider {...methods}>
        <StyledForm noValidate onSubmit={handleSubmit(processFormData)}>
          <InputWrapper>
            <InputText
              label="Captain's email"
              name="email"
              type="email"
              bottomSpacing={smallScreen && !!errors.email}
              id="first_name"
              required="This field is required."
              pattern={/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i}
            />
          </InputWrapper>
          <SubmitButton>SEND REQUEST</SubmitButton>
        </StyledForm>
      </FormProvider>
    </InviteWrapper>
  );
};

const ConfirmationIconStyled = styled(ConfirmationIcon)`
  width: 32px;
  margin-right: 16px;
  fill: #2a8500;
`;

const InvitedInfo = styled.div`
  font-weight: 600;
`;

const InvitedWrapper = styled.h4`
  display: flex;
  align-items: center;
  justify-content: start;
  font-size: 1.125em;
  line-height: 1.333333;
  padding: 40px 48px;
`;

const InviteHeading = styled.h2`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 16px;
  text-align: center;
`;

const InviteWrapper = styled.div`
  margin: 32px 0;
  padding-top: 32px;
  border-top: 1px solid #dbdfe5;
`;

const StyledForm = styled.form`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaMSmMax} {
    flex-direction: column;

    #submitFormButton {
      width: 100%;
    }
  }
`;

const InputWrapper = styled.div`
  flex: 1 1;
  margin-right: 16px;

  ${mediaMSmMax} {
    margin-right: 0;
    margin-bottom: 8px;
    width: 100%;
  }
`;

export default AssignCaptainModal;
